import React from 'react';
import {
  Box,
  Typography,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link as MuiLink,
} from '@mui/material';
import { PrivacyTip } from '@mui/icons-material';
import Header from '../components/Header';

// Shared styling, aligned with the light theme used across AboutPage.
const sectionHeadingSx = {
  fontWeight: 700,
  color: '#0B1929',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
  mt: 6,
  mb: 2,
};

const subHeadingSx = {
  fontWeight: 600,
  color: '#0B1929',
  fontSize: '1.1rem',
  mt: 3,
  mb: 1.5,
};

const bodySx = {
  color: '#64748B',
  fontSize: '1.05rem',
  lineHeight: 1.8,
  mb: 2,
};

const codeSx = {
  fontFamily: '"Roboto Mono", "SFMono-Regular", Menlo, monospace',
  fontSize: '0.95em',
  color: '#0B1929',
  backgroundColor: '#EEF2F7',
  borderRadius: '4px',
  px: 0.6,
  py: 0.1,
};

// Inline monospace span for domains, emails, and registration numbers.
const Code = ({ children }) => (
  <Box component="code" sx={codeSx}>
    {children}
  </Box>
);

const Strong = ({ children }) => (
  <Box component="strong" sx={{ color: '#0B1929', fontWeight: 700 }}>
    {children}
  </Box>
);

function PrivacyPolicyPage() {
  return (
    <div className="PrivacyPolicyPage">
      <Header />

      {/* Hero */}
      <Box sx={{ backgroundColor: '#F8FAFC', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <PrivacyTip sx={{ fontSize: 48, color: '#5090D3' }} />
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: '#0B1929',
                mb: 2,
              }}
            >
              Privacy Notice
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', lineHeight: 1.8 }}>
              <strong>Version:</strong> 0.1&nbsp;&nbsp;·&nbsp;&nbsp;
              <strong>Last updated:</strong> 2 June 2026&nbsp;&nbsp;·&nbsp;&nbsp;
              <strong>Effective from:</strong> 2 June 2026
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Body */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'left' }}>
          {/* 1 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            1. About this notice
          </Typography>
          <Typography sx={bodySx}>
            This privacy notice explains how <Strong>BIM Analytics Ltd</Strong> ("<Strong>BIM</Strong>",
            "<Strong>we</Strong>", "<Strong>us</Strong>", "<Strong>our</Strong>") collects, uses, and
            protects personal data when you visit our website at <Code>bimanalytics.ai</Code>, contact us,
            receive marketing communications from us, work for us, or interact with us in connection with
            our business.
          </Typography>
          <Typography sx={bodySx}>
            BIM Analytics Ltd is registered in England and Wales with company number 15872649. Our
            registered office is at 7 Bell Yard, London, England, WC2A 2JR. We are registered with the UK
            Information Commissioner's Office (ICO) under registration number <Code>CSN9980924</Code>.
          </Typography>
          <Typography sx={bodySx}>
            We are the <Strong>controller</Strong> of the personal data described in this notice.
          </Typography>
          <Typography sx={bodySx}>
            We have not appointed a statutory Data Protection Officer because we are not required to do so
            under Article 37 of the UK GDPR. The point of contact for any data protection question is the
            CEO, who can be reached at <Code>josh@bimanalytics.ai</Code>.
          </Typography>

          {/* 2 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            2. What this notice covers — and what it doesn't
          </Typography>
          <Typography sx={bodySx}>This notice covers the personal data we hold about:</Typography>
          <Box component="ul" sx={{ ...bodySx, pl: 4 }}>
            <li>visitors to <Code>bimanalytics.ai</Code>;</li>
            <li>people who contact us through the website, by email, or at events;</li>
            <li>people we market our services to;</li>
            <li>people who work for us as employees, founders, or contractors.</li>
          </Box>
          <Typography sx={bodySx}>
            It does <Strong>not</Strong> cover personal data that we Process <Strong>on behalf of our
            customers</Strong> when they use our <em>Decode</em> platform. When you log into Decode as a
            user of a BIM customer (for example, an underwriter, broker, or insurance professional whose
            employer has subscribed to Decode), we Process your data as a <Strong>processor</Strong> under
            the agreement between BIM and your employer. That Processing is governed by your employer's Data
            Processing Agreement with BIM, not by this notice. If you have questions about how your
            employer's data — including data about you in your work capacity — is Processed in Decode,
            please contact your employer in the first instance.
          </Typography>

          {/* 3 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            3. Why we need your data and what we collect
          </Typography>
          <Typography sx={bodySx}>We Process personal data for the following purposes:</Typography>

          <Typography sx={subHeadingSx}>3.1 Running our website</Typography>
          <Typography sx={bodySx}>
            When you visit <Code>bimanalytics.ai</Code> we automatically collect minimal technical
            information needed to deliver the site to you, including your IP address and basic request
            information. We do not use analytics or marketing cookies.
          </Typography>

          <Typography sx={subHeadingSx}>3.2 Responding to enquiries you send us</Typography>
          <Typography sx={bodySx}>
            If you contact us through the website, by email, or in person at an event, we collect the
            information you choose to give us, including your name, email address, employer, role, and the
            content of your message.
          </Typography>

          <Typography sx={subHeadingSx}>3.3 Marketing our services to business contacts</Typography>
          <Typography sx={bodySx}>
            We market our services to business contacts in the UK insurance industry. The personal data we
            hold for this purpose typically includes business name, business email address, role, employer,
            and any notes from our prior interaction.
          </Typography>
          <Typography sx={bodySx}>We obtain marketing contacts from:</Typography>
          <Box component="ul" sx={{ ...bodySx, pl: 4 }}>
            <li>people who fill in our contact form or sign up to hear from us;</li>
            <li>public business sources, including LinkedIn Sales Navigator;</li>
            <li>events, conferences, and personal introductions in the Lloyd's market.</li>
          </Box>
          <Typography sx={bodySx}>We do not buy or rent marketing lists.</Typography>

          <Typography sx={subHeadingSx}>3.4 Managing access to the Decode platform</Typography>
          <Typography sx={bodySx}>
            When a customer of BIM creates user accounts for its staff to use Decode, we Process the
            account-holder's name, business email address, role within their organisation, and
            authentication credentials (including multi-factor authentication enrolment data). We Process
            this data on the instruction of, and as processor for, the customer. The data and the account
            itself are managed under that customer's Data Processing Agreement with BIM.
          </Typography>
          <Typography sx={bodySx}>
            The act of logging in — verifying your password and multi-factor authentication code — uses
            strictly necessary session cookies described in §4 below.
          </Typography>

          <Typography sx={subHeadingSx}>3.5 Employing people</Typography>
          <Typography sx={bodySx}>
            For people who work for us as employees, founders, or contractors, we Process the personal data
            we need to manage the employment or engagement relationship. This typically includes name,
            contact details, identification, right-to-work documentation, qualifications, references,
            employment history, payment details, performance records, and any other data necessary for HR,
            payroll, and legal compliance.
          </Typography>

          {/* 4 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            4. Cookies and similar technologies
          </Typography>
          <Typography sx={bodySx}>
            We use only <Strong>strictly necessary cookies</Strong> on <Code>bimanalytics.ai</Code>. These
            are cookies the website cannot function without — for example, the session cookie that keeps you
            signed in after you have logged into Decode, and the security tokens that protect login forms.
          </Typography>
          <Typography sx={bodySx}>
            Because we use only strictly necessary cookies, we do not display a cookie consent banner.
            Strictly necessary cookies are permitted without consent under regulation 6 of the Privacy and
            Electronic Communications (EC Directive) Regulations 2003 (PECR).
          </Typography>
          <Typography sx={bodySx}>
            We do not currently use analytics cookies, marketing cookies, or any third-party tracking
            technologies on <Code>bimanalytics.ai</Code>. If we introduce any in future we will update this
            notice and provide an appropriate consent mechanism.
          </Typography>

          {/* 5 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            5. Our legal basis for Processing your data
          </Typography>
          <Typography sx={bodySx}>
            We rely on the following lawful bases under Article 6 of the UK GDPR, by purpose:
          </Typography>
          <TableContainer sx={{ mb: 3, border: '1px solid #E4E7EB', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#0B1929' }}>Purpose</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#0B1929' }}>Lawful basis</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>Running our website (§3.1)</TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    Legitimate interests — providing a functional public website to communicate about our
                    business.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>Responding to enquiries you send us (§3.2)</TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    Legitimate interests — responding to communication you initiated. Where the enquiry
                    concerns a potential contract with you, taking steps at your request before entering
                    into a contract (Article 6(1)(b)).
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>
                    Marketing our services to business contacts (§3.3)
                  </TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    Legitimate interests — promoting our services to people in roles where our services may
                    be relevant. We have completed a Legitimate Interests Assessment (LIA) supporting this
                    position. You may object at any time (see §8).
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>
                    Managing access to the Decode platform (§3.4)
                  </TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    We Process this data as processor on the instructions of our customer, who is the
                    controller. The lawful basis is determined by the customer.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>Employing people (§3.5)</TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    A combination of: performance of the employment contract (Article 6(1)(b)), compliance
                    with legal obligations (Article 6(1)(c)) such as employment, tax, and immigration law,
                    and legitimate interests (Article 6(1)(f)) for matters supporting the employment
                    relationship.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={bodySx}>
            Marketing emails we send to business contacts comply with PECR, including the right to opt out
            at any time. Every marketing email we send includes a clear unsubscribe option. You can also ask
            us to stop at any time by emailing <Code>josh@bimanalytics.ai</Code>.
          </Typography>

          {/* 6 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            6. How long we keep your data
          </Typography>
          <Typography sx={bodySx}>
            We keep personal data only for as long as we need it for the purposes we collected it for, or
            for as long as we are required to by law. Our standard retention positions are:
          </Typography>
          <TableContainer sx={{ mb: 3, border: '1px solid #E4E7EB', borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 700, color: '#0B1929' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#0B1929' }}>Retention</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>Website access logs</TableCell>
                  <TableCell sx={{ color: '#64748B' }}>12 months.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>Inbound enquiries you send us</TableCell>
                  <TableCell sx={{ color: '#64748B' }}>24 months from your last contact with us.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>Marketing contacts</TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    Until you withdraw consent or object, or after 24 months of no engagement (no opens, no
                    clicks, no replies), whichever is sooner.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>
                    Decode user account data (Processed as processor)
                  </TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    For the duration of your employer's contract with BIM, then deleted or returned per the
                    Data Processing Agreement.
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>
                    Employee, founder, and contractor records
                  </TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    For the duration of the engagement, plus the period required by UK tax, employment, and
                    pensions law (typically six years after the engagement ends).
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ color: '#64748B' }}>Job applicant data</TableCell>
                  <TableCell sx={{ color: '#64748B' }}>
                    We do not currently accept job applications through <Code>bimanalytics.ai</Code>. If you
                    apply for a role with us through another channel, application records are kept for 12
                    months after the outcome is communicated, or longer if you join BIM.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography sx={bodySx}>
            These windows may be extended where we are required to retain data for the defence of legal
            claims or to comply with a specific legal obligation.
          </Typography>

          {/* 7 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            7. Where your data is processed and stored
          </Typography>
          <Typography sx={bodySx}>
            We Process most personal data in the United Kingdom. Some categories of personal data are
            Processed outside the UK by our service providers (see §8 below). Where this happens, we ensure
            an appropriate transfer mechanism is in place under UK GDPR — typically the UK International Data
            Transfer Agreement (UK IDTA) or the UK Addendum to the EU Standard Contractual Clauses,
            supplemented by additional contractual and technical safeguards from the relevant provider.
          </Typography>
          <Typography sx={bodySx}>
            We do not transfer personal data outside the UK without such a mechanism in place.
          </Typography>

          {/* 8 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            8. Service providers we use
          </Typography>
          <Typography sx={bodySx}>
            We use the following categories of service providers for our controller-role Processing
            (separate from the sub-processors we use to deliver the <em>Decode</em> platform, which are
            listed in our Sub-Processor List provided to customers):
          </Typography>
          <Box component="ul" sx={{ ...bodySx, pl: 4 }}>
            <li>
              <Strong>Google Workspace (Google LLC)</Strong> — business email, calendar, document storage,
              and spreadsheets we use to manage our CRM and internal records. Personal data we hold for
              marketing and enquiries is held within Google Workspace. Google's terms of service and data
              protection terms apply.
            </li>
          </Box>
          <Typography sx={bodySx}>
            We do not currently use any other third-party service providers as part of our controller-role
            Processing. Our website contact form is built directly into our website and submits enquiries to
            our own infrastructure (Google Cloud, UK-resident), rather than to a third-party form handler.
          </Typography>
          <Typography sx={bodySx}>We will not:</Typography>
          <Box component="ul" sx={{ ...bodySx, pl: 4 }}>
            <li>sell or rent your personal data to third parties;</li>
            <li>share your personal data with third parties for their own marketing purposes.</li>
          </Box>
          <Typography sx={bodySx}>
            We may share personal data where we are required to do so by law (for example, by court order,
            regulatory request, or to prevent fraud or other crime).
          </Typography>

          {/* 9 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            9. How we protect your data
          </Typography>
          <Typography sx={bodySx}>
            We are committed to keeping your data secure. We maintain an Information Security Management
            System aligned with ISO/IEC 27001:2022. Our security controls include multi-factor
            authentication on all systems containing personal data, encryption of data in transit and at
            rest, role-based access control, regular vulnerability scanning, and documented incident
            response procedures.
          </Typography>
          <Typography sx={bodySx}>
            For more information about our security posture, see our security overview, available on
            request, or contact us at <Code>security@bimanalytics.ai</Code>.
          </Typography>

          {/* 10 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            10. Your rights
          </Typography>
          <Typography sx={bodySx}>
            Under UK data protection law you have a number of rights in relation to personal data we hold
            about you. You have the right to ask us to:
          </Typography>
          <Box component="ul" sx={{ ...bodySx, pl: 4 }}>
            <li>give you information about the personal data we Process about you;</li>
            <li>give you a copy of that data;</li>
            <li>correct any personal data that is inaccurate;</li>
            <li>erase your personal data where there is no longer a justification for Processing it;</li>
            <li>restrict our Processing of your personal data in certain circumstances;</li>
            <li>object to our Processing of your personal data, including for direct marketing.</li>
          </Box>
          <Typography sx={bodySx}>
            Where we rely on your consent for any Processing, you have the right to withdraw that consent at
            any time, and the right to receive a copy of the data you provided in a structured, commonly
            used, and machine-readable format.
          </Typography>
          <Typography sx={bodySx}>
            To exercise any of these rights, please contact <Code>josh@bimanalytics.ai</Code>. We will
            respond to your request within one month, in line with UK GDPR requirements.
          </Typography>

          {/* 11 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            11. Automated decision-making
          </Typography>
          <Typography sx={bodySx}>
            BIM does not carry out solely automated decision-making with legal or similarly significant
            effects on individuals in the scope of this notice. Our marketing decisions, sales decisions,
            and employment decisions involve human review.
          </Typography>

          {/* 12 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            12. Special category and criminal-offence data
          </Typography>
          <Typography sx={bodySx}>
            We do not knowingly collect or Process special category personal data (such as data about your
            health, racial or ethnic origin, religion, or sexual orientation) or data relating to criminal
            convictions and offences via our website or in our marketing. Where we Process such data in the
            course of employment (for example, equality and diversity monitoring, or sickness records), we
            do so under the conditions in Article 9 of the UK GDPR and Schedule 1 of the Data Protection Act
            2018, with the lawful basis recorded internally.
          </Typography>
          <Typography sx={bodySx}>
            We Process such data for our customers in connection with Decode where the customer has uploaded
            it; that Processing is governed by the customer's Data Processing Agreement, not this notice.
          </Typography>

          {/* 13 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            13. Children
          </Typography>
          <Typography sx={bodySx}>
            Our services and website are aimed at business users and are not directed at children. We do not
            knowingly collect personal data from children.
          </Typography>

          {/* 14 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            14. Changes to this notice
          </Typography>
          <Typography sx={bodySx}>
            We may update this notice from time to time. When we do, we will update the "Last updated" date
            at the top of this notice. If the changes affect how we Process your personal data in a material
            way, we will take reasonable steps to bring them to your attention.
          </Typography>
          <Typography sx={bodySx}>
            The previous version of this notice is available on request from <Code>josh@bimanalytics.ai</Code>.
          </Typography>

          {/* 15 */}
          <Typography variant="h3" sx={sectionHeadingSx}>
            15. Questions and complaints
          </Typography>
          <Typography sx={bodySx}>
            If you have any questions about this notice, or about how we Process your personal data, please
            contact:
          </Typography>
          <Box component="ul" sx={{ ...bodySx, pl: 4 }}>
            <li>
              <Strong>Email:</Strong> <Code>josh@bimanalytics.ai</Code>
            </li>
            <li>
              <Strong>Post:</Strong> Joshua O'Hara, BIM Analytics Ltd, 7 Bell Yard, London, England, WC2A
              2JR
            </li>
          </Box>
          <Typography sx={bodySx}>
            If you are not satisfied with how we have handled a question or complaint, you have the right to
            make a complaint to the UK Information Commissioner's Office (ICO):
          </Typography>
          <Box component="ul" sx={{ ...bodySx, pl: 4 }}>
            <li>
              <Strong>Web:</Strong>{' '}
              <MuiLink href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" sx={{ color: '#5090D3' }}>
                ico.org.uk
              </MuiLink>
            </li>
            <li>
              <Strong>Phone:</Strong> 0303 123 1113
            </li>
            <li>
              <Strong>Post:</Strong> Information Commissioner's Office, Wycliffe House, Water Lane, Wilmslow,
              Cheshire, SK9 5AF
            </li>
          </Box>
          <Typography sx={bodySx}>
            Making a complaint to the ICO does not affect your right to seek redress through the courts.
          </Typography>

          {/* Attribution */}
          <Box sx={{ mt: 6, pt: 3, borderTop: '1px solid #E4E7EB' }}>
            <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem', lineHeight: 1.7 }}>
              This privacy notice contains text adapted from the GOV.UK Design System privacy notice (
              <MuiLink
                href="https://design-system.service.gov.uk/privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#5090D3' }}
              >
                design-system.service.gov.uk/privacy-policy/
              </MuiLink>
              ), which is © Crown copyright and licensed under the{' '}
              <MuiLink
                href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#5090D3' }}
              >
                Open Government Licence v3.0
              </MuiLink>
              .
            </Typography>
          </Box>
        </Container>
      </Box>
    </div>
  );
}

export default PrivacyPolicyPage;
